/**
 * One-time prompt for members who joined before gender / birth year existed (migration 0015).
 *
 * It appears once, after onboarding and after the policy gate, and closes for good as soon as it is
 * submitted — `demographics_asked_at` is stamped by the server whatever the answer was, so someone who
 * chooses "ไม่ระบุ" is never asked again. Being asked twice after declining is how a product teaches people
 * that saying no doesn't work.
 *
 * Blocking, like PolicyGate: the question must be answered to carry on. But "ไม่ระบุทั้งสองข้อ" is a
 * one-tap answer of equal weight, and it settles the matter permanently. That combination is the point —
 * everyone is ASKED (which is what makes the sample worth charting), and nobody is made to disclose
 * anything (which is what keeps the consent real and the data honest). A gate with no way to decline would
 * produce a database full of guesses.
 */
import { useEffect, useState } from 'preact/hooks';
import type { Gender } from '@shared/types';
import { patch, errorMessage } from '../api';
import { useAuth } from '../state/auth';
import { DemographicsFields } from './Demographics';
import { ErrorBox } from './ui';

export function DemographicsGate() {
  const { me, refresh } = useAuth();
  const [gender, setGender] = useState<Gender | null>(null);
  const [birthYear, setBirthYear] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Onboarding already asks, so only members who finished it before the question existed see this.
  const open = !!me && me.onboarded && me.policy_accepted && !me.demographics_asked
    && me.role !== 'banned' && me.role !== 'deleted';

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (!open) return null;

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await patch('/api/me', { gender, birth_year: birthYear });
      await refresh();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div class="sheet-back" role="presentation">
      <div class="sheet" role="dialog" aria-modal="true" aria-labelledby="demog-gate-title">
        <div class="grip" />
        <h2 id="demog-gate-title">ขอถามอีกสองข้อ</h2>
        <p class="muted small">
          ตอนคุณสมัคร เรายังไม่ได้ถามสองข้อนี้ ตอบแล้วช่วยให้เราจัดตี้และอีเว้นท์ให้ตรงกับคนที่เล่นจริง ๆ
          — ไม่บังคับ ไม่แสดงบนโปรไฟล์ และแก้ทีหลังได้ที่หน้าโปรไฟล์
        </p>
        <div class="mt-16">
          <DemographicsFields compact value={{ gender, birthYear }} onChange={(v) => { setGender(v.gender); setBirthYear(v.birthYear); }} />
        </div>
        {error && <div class="mt-8"><ErrorBox message={error} /></div>}
        <button type="button" class="btn primary lg block mt-16" disabled={busy} onClick={submit}>
          {busy ? 'กำลังบันทึก…' : gender || birthYear ? 'บันทึก' : 'ไม่ระบุทั้งสองข้อ'}
        </button>
        <p class="tiny muted mt-8" style={{ textAlign: 'center' }}>ตอบแล้วจะไม่ถามอีก</p>
      </div>
    </div>
  );
}
